const AIRWALLEX_SDK_URL = "https://static.airwallex.com/components/sdk/v1/index.js";
const SDK_LOAD_TIMEOUT_MS = 15000;

// "prod" or "demo". Defaults to demo so a missing variable never hits live payments.
const AIRWALLEX_ENV = process.env.REACT_APP_AIRWALLEX_ENV === "prod" ? "prod" : "demo";

let sdkPromise = null;

// Injects the CDN script once; cached as a promise so two fast clicks share one
// script tag. Cleared on failure so a retry can load it again.
export const loadAirwallexSdk = () => {
  if (window.AirwallexComponentsSDK) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      sdkPromise = null;
      reject(new Error("Timed out loading Airwallex SDK"));
    }, SDK_LOAD_TIMEOUT_MS);

    const onLoad = () => {
      clearTimeout(timeout);
      resolve();
    };
    const onError = () => {
      clearTimeout(timeout);
      sdkPromise = null;
      reject(new Error("Failed to load Airwallex SDK"));
    };

    const existing = document.querySelector(`script[src="${AIRWALLEX_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = AIRWALLEX_SDK_URL;
    script.async = true;
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    document.head.appendChild(script);
  });

  return sdkPromise;
};

// The CDN script is the Components SDK, which exposes window.AirwallexComponentsSDK.
// init() is async and returns { payments }; redirectToCheckout lives on that handle.
// Never resolves on success because the browser navigates away, so keep loading state
// on and clear it only on error.
export const redirectToAirwallexCheckout = async (data) => {
  await loadAirwallexSdk();

  if (!window.AirwallexComponentsSDK) {
    throw new Error("Airwallex SDK unavailable after load");
  }

  const { payments } = await window.AirwallexComponentsSDK.init({
    env: AIRWALLEX_ENV,
    enabledElements: ["payments"],
  });

  // The hosted page only redirects to successUrl/failUrl, and Airwallex rejects
  // non-HTTPS ones, so they are sent only on HTTPS origins. On http://localhost they
  // are omitted and the intent's own server-side return_url is used.
  const isHttps = window.location.protocol === "https:";

  //HEADER LOGO OF THE HOSTED PAGE. A backend-provided logo_url WINS, ELSE THE FILE IN public/ (AIRWALLEX FETCHES IT, SO IT NEEDS A PUBLIC HTTPS SITE)
  const logoUrl = data.logo_url || `${window.location.origin}/warely_logo.png`;

  await payments.redirectToCheckout({
    env: AIRWALLEX_ENV,
    mode: "payment",
    intent_id: data.payment_intent_id,
    client_secret: data.client_secret,
    currency: data.currency || "SGD",
    logoUrl,
    ...(isHttps && data.return_url ? { successUrl: data.return_url } : {}),
    ...(isHttps && data.fail_url ? { failUrl: data.fail_url } : {}),
  });
};

// The backend answers 200 with status:"failed" for shopper-actionable cases, so only
// the parsed `status` decides success vs failure.
export const readCheckoutResponse = (result) => {
  if (
    result?.status === "success" &&
    result?.data?.payment_intent_id &&
    result?.data?.client_secret
  ) {
    return { ok: true, data: result.data };
  }

  return {
    ok: false,
    message:
      result?.message ||
      result?.errorMessage ||
      result?.msg ||
      "Airwallex payment request failed",
  };
};

// Pulls the most useful message out of a rejected axios call (non-2xx or network error).
export const readApiError = (error) => {
  const body = error?.response?.data;
  return (
    body?.message ||
    body?.errorMessage ||
    body?.msg ||
    (typeof body === "string" && body.length < 200 ? body : null) ||
    error?.message ||
    "Airwallex payment request failed"
  );
};
